import { z } from 'zod';
import { isCustomFieldValueEmpty } from '../custom-fields/custom-field-schema.js';
import { resolveVisibility } from '../custom-fields/resolve-visibility.js';
import { createSortablePaginationSchema, createStringFilterSchema, dateFilterSchema, idFilterSchema, SecurityLevel, } from '../shared/index.js';
export var LegislativeItemSystemStatus;
(function (LegislativeItemSystemStatus) {
    LegislativeItemSystemStatus["Draft"] = "draft";
    LegislativeItemSystemStatus["Review"] = "review";
    LegislativeItemSystemStatus["Deliberation"] = "deliberation";
    LegislativeItemSystemStatus["Processing"] = "processing";
    LegislativeItemSystemStatus["ClosedRejected"] = "closed_rejected";
    LegislativeItemSystemStatus["ClosedApproved"] = "closed_approved";
    LegislativeItemSystemStatus["ClosedCancelled"] = "closed_cancelled";
})(LegislativeItemSystemStatus || (LegislativeItemSystemStatus = {}));
export var LegislativeItemFilterTypeEnum;
(function (LegislativeItemFilterTypeEnum) {
    LegislativeItemFilterTypeEnum["ALL"] = "all";
    LegislativeItemFilterTypeEnum["MY_ITEMS"] = "my-items";
    LegislativeItemFilterTypeEnum["THIS_WEEK"] = "this-week";
    LegislativeItemFilterTypeEnum["PENDING_REVIEW"] = "pending-review";
    LegislativeItemFilterTypeEnum["RECENTLY_MODIFIED"] = "recently-modified";
    LegislativeItemFilterTypeEnum["SCHEDULED_FOR_DELETION"] = "scheduled-for-deletion";
})(LegislativeItemFilterTypeEnum || (LegislativeItemFilterTypeEnum = {}));
const legislativeItemSystemStatusFilterSchema = createStringFilterSchema(z.enum(LegislativeItemSystemStatus));
export const LEGISLATIVE_ITEM_ORDER_BY_FIELDS = [
    'code',
    'createdAt',
    'shortTitle',
    'targetMeetingDate',
    'updatedAt',
];
export const legislativeItemQueryParamsSchema = createSortablePaginationSchema(LEGISLATIVE_ITEM_ORDER_BY_FIELDS).extend({
    searchTerm: z.string().optional(),
    targetMeetingDate: dateFilterSchema.optional(),
    targetMeetingId: idFilterSchema.optional(),
    legislativeStatusId: idFilterSchema.optional(),
    status: legislativeItemSystemStatusFilterSchema.optional(),
    legislativeTypeId: idFilterSchema.optional(),
    departmentId: idFilterSchema.optional(),
    meetingBodyId: idFilterSchema.optional(),
    staffContactId: idFilterSchema.optional(),
    filterType: z.enum(LegislativeItemFilterTypeEnum).optional(),
    AND: z
        .array(z.object({
        searchTerm: z.string().optional(),
        targetMeetingDate: dateFilterSchema.optional(),
        legislativeStatusId: idFilterSchema.optional(),
        status: legislativeItemSystemStatusFilterSchema.optional(),
        departmentId: idFilterSchema.optional(),
        legislativeTypeId: idFilterSchema.optional(),
        meetingBodyId: idFilterSchema.optional(),
        staffContactId: idFilterSchema.optional(),
    }))
        .optional(),
    OR: z
        .array(z.object({
        searchTerm: z.string().optional(),
        targetMeetingDate: dateFilterSchema.optional(),
        legislativeStatusId: idFilterSchema.optional(),
        status: legislativeItemSystemStatusFilterSchema.optional(),
        departmentId: idFilterSchema.optional(),
        legislativeTypeId: idFilterSchema.optional(),
        meetingBodyId: idFilterSchema.optional(),
        staffContactId: idFilterSchema.optional(),
    }))
        .optional(),
});
// Custom field instance value schema for both create and update operations
const customFieldInstanceValueSchema = z.object({
    id: z.number().optional().nullable(), // Instance ID for updates (null for create)
    customFieldId: z.number().min(1, 'Custom field ID is required'), // Custom field ID for both create and update
    value: z.unknown(), // JSON value - can be string, number, boolean, array, or object
});
const sponsorSchema = z
    .object({
    userId: z.number().int().positive().nullish(),
    departmentId: z.number().int().positive().nullish(),
    meetingMemberId: z.number().int().positive().nullish(),
    isCoSponsor: z.boolean(),
})
    .refine(({ departmentId, meetingMemberId, userId }) => [userId, departmentId, meetingMemberId].filter((id) => id !== null && id !== undefined)
    .length === 1, {
    message: 'A sponsor must be exactly one of a user, a department, or a meeting member',
});
const staffContactIdsSchema = z
    .array(z.number().int().positive())
    .max(50)
    .transform((ids) => [...new Set(ids)]);
/**
 * Drops the superseded single-value `staffContactId`. It stays declared on the schemas purely so
 * that a client still sending it is not rejected; the value is discarded here and never reaches
 * the API, which reads `staffContactIds` alone.
 */
const omitDeprecatedStaffContactId = (data) => {
    const { staffContactId: _ignored, ...rest } = data;
    return rest;
};
/** Base object schema for legislative item creation fields (plain ZodObject, no refinements). */
export const legislativeItemBaseSchema = z.object({
    // Item Details
    code: z.string('Item ID is required').optional(),
    shortTitle: z.string().optional().nullable(),
    fullTitle: z.string('Full title is required').min(1, 'Full title is required'),
    summary: z.string().optional().nullable(),
    fiscalNote: z.string().optional().nullable(),
    internalNotes: z
        .string()
        .optional()
        .nullable()
        .describe('Notes for internal staff and meeting members. Never exposed publicly. Use HTML.'),
    // Item Type (Required)
    legislativeTypeId: z.number().min(1, 'Item type is required'),
    // Administrative Details
    departmentId: z.number().optional().nullable(),
    staffContactIds: staffContactIdsSchema.optional(),
    staffContactId: z
        .number()
        .optional()
        .nullable()
        .describe('Ignored. Superseded by staffContactIds; accepted only so older clients still pass.'),
    recommendedActionId: z.number().optional().nullable(),
    recommendedActionText: z.string().optional().nullable(),
    securityLevel: z.enum(SecurityLevel).optional(),
    allowedUserIds: z.array(z.number().int().positive()).optional(),
    isPubliclyReleased: z.boolean().optional(),
    // Special Requests
    executiveSession: z.boolean().optional(),
    requestingEmergency: z.boolean().optional(),
    requestingConsent: z.boolean().optional(),
    publicHearing: z.boolean().optional(),
    // Meeting Assignment
    meetingBodyIds: z.array(z.number()),
    targetMeetingDate: z.iso
        .datetime()
        .nullish()
        .describe('The date of the target meeting where the item will be introduced. This value comes from the meeting date field.'),
    targetMeetingId: z
        .number()
        .nullish()
        .describe('The ID of the target meeting where the item will be introduced'),
    // Custom Field Values
    customFieldValues: z.array(customFieldInstanceValueSchema).optional(),
    // Auto Numbering
    autoNumberingConfigId: z.number().optional().nullable(),
    // Sponsors
    legislativeItemSponsors: z.array(sponsorSchema).optional(),
});
// Refined base schema (for form validation)
const baseItemSchema = legislativeItemBaseSchema.refine((data) => {
    // If targetMeetingDate is set, targetMeetingId must also be set
    if (data.targetMeetingDate && data.targetMeetingDate !== '') {
        return data.targetMeetingId && data.targetMeetingId > 0;
    }
    return true;
}, {
    message: 'Target meeting must be selected when target meeting date is set',
    path: ['targetMeetingId'],
});
// Update schema without legislativeTypeId (since item types cannot be changed)
const updateLegislativeItemObjectSchema = z.object({
    // Item Details
    code: z.string().min(1, 'Item ID is required'),
    shortTitle: z.string().optional().nullable(),
    fullTitle: z.string().min(1, 'Full title is required'),
    summary: z.string().optional().nullable(),
    fiscalNote: z.string().optional().nullable(),
    internalNotes: z
        .string()
        .optional()
        .nullable()
        .describe('Notes for internal staff and meeting members. Never exposed publicly. Use HTML.'),
    // Administrative Details
    departmentId: z.number().optional().nullable(),
    staffContactIds: staffContactIdsSchema.optional(),
    staffContactId: z
        .number()
        .optional()
        .nullable()
        .describe('Ignored. Superseded by staffContactIds; accepted only so older clients still pass.'),
    recommendedActionId: z.number().optional().nullable(),
    recommendedActionText: z.string().optional().nullable(),
    securityLevel: z.enum(SecurityLevel).optional(),
    allowedUserIds: z.array(z.number().int().positive()).optional(),
    isPubliclyReleased: z.boolean().optional(),
    // Special Requests
    executiveSession: z.boolean(),
    requestingEmergency: z.boolean(),
    requestingConsent: z.boolean(),
    publicHearing: z.boolean(),
    // Meeting Assignment
    meetingBodyIds: z.array(z.number()),
    targetMeetingDate: z.iso
        .datetime()
        .nullish()
        .describe('The date of the target meeting where the item will be introduced. This value comes from the meeting date field, the meeting being the one specified in targetMeetingId'),
    targetMeetingId: z
        .number()
        .nullish()
        .describe('The ID of the target meeting where the item will be introduced'),
    // Custom Field Values (using unified schema for both create and update)
    customFieldValues: z.array(customFieldInstanceValueSchema).optional(),
    // Auto Numbering
    autoNumberingConfigId: z.number().optional().nullable(),
    // Sponsors
    legislativeItemSponsors: z.array(sponsorSchema).optional(),
});
// Refinement function for target meeting validation
const targetMeetingRefinement = (data) => {
    // If targetMeetingDate is set, targetMeetingId must also be set
    if (data.targetMeetingDate && data.targetMeetingDate !== '') {
        return (data.targetMeetingId !== undefined &&
            data.targetMeetingId !== null &&
            data.targetMeetingId > 0);
    }
    return true;
};
const targetMeetingRefinementConfig = {
    message: 'Target meeting must be selected when target meeting date is set',
    path: ['targetMeetingId'],
};
export const changeItemTypeSchema = z.object({
    legislativeTypeId: z.number().min(1, 'Item type is required'),
});
export const changeItemStatusSchema = z.object({
    status: z.enum(LegislativeItemSystemStatus),
});
export const copyLegislativeItemSchema = z.object({
    copyAttachments: z.boolean().default(false),
});
// API schema with transformations (for API validation)
export const createLegislativeItemSchema = baseItemSchema.transform((data) => ({
    ...omitDeprecatedStaffContactId(data),
    targetMeetingDate: data.targetMeetingDate === '' ? null : data.targetMeetingDate,
    allowedUserIds: data.securityLevel === SecurityLevel.RESTRICTED ? (data.allowedUserIds ?? []) : [],
}));
// For updates, we need to handle partial data with transformations
// Apply .partial() to the object schema first, then add refinement and transform
export const updateLegislativeItemSchema = updateLegislativeItemObjectSchema
    .partial()
    .refine(targetMeetingRefinement, targetMeetingRefinementConfig)
    .transform((data) => ({
    ...omitDeprecatedStaffContactId(data),
    targetMeetingDate: data.targetMeetingDate === '' ? null : data.targetMeetingDate,
    ...(data.securityLevel !== undefined && data.securityLevel !== SecurityLevel.RESTRICTED
        ? { allowedUserIds: [] }
        : {}),
}));
// Form schemas without transformations (for react-hook-form)
export const createLegislativeItemFormSchema = baseItemSchema;
export const updateLegislativeItemFormSchema = updateLegislativeItemObjectSchema
    .partial()
    .refine(targetMeetingRefinement, targetMeetingRefinementConfig);
export * from './legislative-items-attachments/index.js';
export * from './legislative-items-documents/index.js';
export * from './legislative-items-document-checkouts/index.js';
export var MyAssignmentsAssignmentFilter;
(function (MyAssignmentsAssignmentFilter) {
    MyAssignmentsAssignmentFilter["Pending"] = "pending";
    MyAssignmentsAssignmentFilter["Past"] = "past";
    MyAssignmentsAssignmentFilter["Future"] = "future";
})(MyAssignmentsAssignmentFilter || (MyAssignmentsAssignmentFilter = {}));
export const myAssignmentsBodySchema = legislativeItemQueryParamsSchema.extend({
    assignmentFilter: z
        .array(z.enum(MyAssignmentsAssignmentFilter))
        .min(1, 'At least one assignment filter is required'),
    userIds: z.array(z.number().int().positive()).optional(),
    /** Match every assignee in the government; `userIds` is ignored. */
    allAssignees: z.boolean().optional(),
});
/**
 * Stable keys for the data each My Assignments visual aggregates. The frontend
 * catalog (which may render the same key in more than one way, e.g. overdue as a
 * count and as a percentage) maps its entries onto these keys, and the backend
 * computes only the requested ones.
 */
export var MyAssignmentsVisualKey;
(function (MyAssignmentsVisualKey) {
    /** Chart: assignments grouped by assignment status (pending/approved/rejected). */
    MyAssignmentsVisualKey["ByStatus"] = "by-status";
    /** Chart: assignments (in the active tab) grouped by their step's type. */
    MyAssignmentsVisualKey["ByStepType"] = "by-step-type";
    /** Chart: assignments (in the active tab) bucketed by due date. */
    MyAssignmentsVisualKey["ByDueDate"] = "by-due-date";
    /** Chart: assignments (in the active tab) grouped by assignee. */
    MyAssignmentsVisualKey["ByAssignee"] = "by-assignee";
    /** Chart: assignments (in the active tab) grouped by legislative type. */
    MyAssignmentsVisualKey["ByType"] = "by-type";
    /** Stat: total assignments in the active tab. */
    MyAssignmentsVisualKey["Total"] = "total";
    /** Stat: overdue open assignments (`value`) as a share of open assignments (`total`). */
    MyAssignmentsVisualKey["Overdue"] = "overdue";
    /** Stat: open assignments due within the next 7 days. */
    MyAssignmentsVisualKey["DueThisWeek"] = "due-this-week";
    /** Stat: assignments completed in the last 7 days. */
    MyAssignmentsVisualKey["CompletedThisWeek"] = "completed-this-week";
    /** Stat: approval rate (approved as a share of decided assignments). */
    MyAssignmentsVisualKey["ApprovalRate"] = "approval-rate";
    /** Agenda: the soonest-due open assignments. */
    MyAssignmentsVisualKey["Upcoming"] = "upcoming";
    /** Agenda: the most recently completed assignments. */
    MyAssignmentsVisualKey["RecentlyCompleted"] = "recently-completed";
})(MyAssignmentsVisualKey || (MyAssignmentsVisualKey = {}));
/** Bucket ids used by the {@link MyAssignmentsVisualKey.ByDueDate} visual. */
export var MyAssignmentsDueDateBucket;
(function (MyAssignmentsDueDateBucket) {
    MyAssignmentsDueDateBucket["Overdue"] = "overdue";
    MyAssignmentsDueDateBucket["ThisWeek"] = "this-week";
    MyAssignmentsDueDateBucket["Later"] = "later";
    MyAssignmentsDueDateBucket["None"] = "none";
})(MyAssignmentsDueDateBucket || (MyAssignmentsDueDateBucket = {}));
/**
 * Request body for the My Assignments visuals aggregation endpoint. The item/people
 * filters and `userIds` always apply. `assignmentFilter` (the pending/past/future
 * tab) is optional and applies only to tab-scoped visuals (`by-type`); the rest
 * carry their own status/date scope and ignore it.
 */
export const myAssignmentsVisualsBodySchema = legislativeItemQueryParamsSchema.extend({
    assignmentFilter: z
        .array(z.enum(MyAssignmentsAssignmentFilter))
        .min(1, 'At least one assignment filter is required'),
    userIds: z.array(z.number().int().positive()).optional(),
    /** Match every assignee in the government; `userIds` is ignored. */
    allAssignees: z.boolean().optional(),
    visualKeys: z.array(z.enum(MyAssignmentsVisualKey)).min(1, 'At least one visual key is required'),
});
/** Fields required for submit-for-review. Add entries to extend. */
const SUBMIT_FOR_REVIEW_REQUIRED_FIELDS = [
    { field: 'code', label: 'Item ID' },
    { field: 'fullTitle', label: 'Full Title' },
    { field: 'meetingBodies', label: 'Meeting Body' },
    { field: 'departmentId', label: 'Department' },
    { field: 'staffContacts', label: 'Staff Contacts' },
];
/** Returns `{ field, label }[]` for each missing required field, including custom fields. */
export function validateSubmitForReview(item, customFieldInstances) {
    const missing = [];
    // Check standard required fields
    for (const { field, label } of SUBMIT_FOR_REVIEW_REQUIRED_FIELDS) {
        const value = item[field];
        if (value === null ||
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)) {
            missing.push({ field, label });
        }
    }
    // Check required custom fields
    if (customFieldInstances && customFieldInstances.length > 0) {
        // Resolve visibility for conditional fields
        const fields = customFieldInstances.map((instance) => ({
            id: instance.id,
            customFieldId: instance.customFieldId,
            parentId: instance.parentConditionInstanceId ?? null,
            parentConditionValue: (instance.parentConditionInstanceValue ?? null),
        }));
        const values = customFieldInstances.map((instance) => ({
            id: instance.id,
            customFieldId: instance.customFieldId,
            value: instance.value,
        }));
        const rawVisibility = resolveVisibility(fields, values);
        // OR-reduce visibility by customFieldId (same field can appear across meeting bodies)
        const visibleByCustomFieldId = new Map();
        const requiredByCustomFieldId = new Map();
        for (const instance of customFieldInstances) {
            const isVisible = rawVisibility[instance.id] ?? false;
            visibleByCustomFieldId.set(instance.customFieldId, (visibleByCustomFieldId.get(instance.customFieldId) ?? false) || isVisible);
            if (isVisible && instance.required) {
                requiredByCustomFieldId.set(instance.customFieldId, true);
            }
        }
        const checked = new Set();
        for (const instance of customFieldInstances) {
            if (checked.has(instance.customFieldId))
                continue;
            checked.add(instance.customFieldId);
            if (!visibleByCustomFieldId.get(instance.customFieldId))
                continue;
            if (!requiredByCustomFieldId.get(instance.customFieldId))
                continue;
            if (isCustomFieldValueEmpty(instance.value, instance.type)) {
                missing.push({
                    field: `custom_field:${instance.customFieldId}`,
                    label: instance.name,
                });
            }
        }
    }
    return missing;
}
/**
 * Schema for applying a legislative action to an item (creating a
 * `LegislativeItemAppliedAction`). The result status is derived server-side from
 * the action's configured pass status, and the action is never tied to a motion
 * here, so neither is part of the request body.
 */
export const createLegislativeItemActionSchema = z.object({
    legislativeActionId: z.number().int().min(1, 'Legislative action is required'),
    note: z.string().max(4000, 'The note is too long').optional(),
});
export const legislativeItemAgendaRecommendationQuerySchema = z.object({
    meetingEventId: z.coerce.number().int().positive(),
});
export const legislativeItemMeetingAgendaQuerySchema = z.object({
    legislativeItemId: z.coerce.number().int().positive().optional(),
});
export const legislativeItemAgendaPlacementSchema = z.object({
    legislativeItemId: z.number().int().positive(),
    agendaSectionId: z.number().int().positive(),
});
export const legislativeItemAgendaPlacementCreateSchema = legislativeItemAgendaPlacementSchema.extend({
    meetingBodyIds: z.array(z.number().int()),
});
